import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  Ban,
  CheckCircle2,
  Loader2,
  Pause,
  Play,
  Save,
  Send,
  Trash2,
  Users,
  FileText,
  MousePointerClick,
  Sparkles,
  X,
} from 'lucide-react'
import Modal from '../components/ui/Modal'
import { api, apiErrorMessage } from '../lib/api'
import { confirmDialog, toast } from '../lib/dialogs'
import { useAuth } from '../contexts/AuthContext'
import { PILL_CAMPAIGN, PILL_RECIPIENT } from '../components/marketing/campaign-status'
import { htmlATexto, textoAHtml } from '../lib/marketing-body'
import {
  MARKETING_DAILY_CAP_MAX,
  MARKETING_THROTTLE_MIN,
  VARIABLES_DE_CAMPANA,
  type DraftMarketingCampaignResponse,
  type ClientTagListResponse,
  type MarketingCampaignDetailResponse,
  type MarketingSenderListResponse,
  type MarketingTemplateDto,
  type MarketingTemplateListResponse,
  type SetMarketingAudienceResponse,
} from '@toga/shared'

/**
 * La pantalla de trabajo de una campaña: el texto, la lista y el marcador.
 *
 * Mientras está en BORRADOR se puede tocar todo. En cuanto sale el primer
 * correo, el texto se congela —el histórico tiene que parecerse a lo que la
 * gente recibió— y lo único que queda editable es el ritmo, que es justo lo que
 * hace falta poder frenar sobre la marcha.
 */
export default function MarketingCampaignDetail() {
  const { id = '' } = useParams()
  const { t, i18n } = useTranslation(['marketing', 'common'])
  const navigate = useNavigate()
  const auth = useAuth()
  const role = auth.status === 'authenticated' ? auth.user.role : null
  const canWrite = role === 'ADMIN' || role === 'CONSULTOR' || role === 'ANALISTA'
  const canSend = role === 'ADMIN' || role === 'CONSULTOR'

  const [data, setData] = useState<MarketingCampaignDetailResponse | null>(null)
  const [tags, setTags] = useState<ClientTagListResponse['items']>([])
  const [senders, setSenders] = useState<MarketingSenderListResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [lanzando, setLanzando] = useState(false)

  // Borrador editable en local; solo se manda al pulsar Guardar.
  const [form, setForm] = useState({
    name: '',
    subject: '',
    bodyText: '',
    senderAccountId: '',
    dailyCap: 150,
    throttleSeconds: 20,
    trackClicks: true,
  })
  const [tagIds, setTagIds] = useState<string[]>([])
  const [extraEmails, setExtraEmails] = useState('')
  const [ultimaAudiencia, setUltimaAudiencia] = useState<SetMarketingAudienceResponse | null>(null)
  const [plantillas, setPlantillas] = useState<MarketingTemplateDto[]>([])
  const [redactando, setRedactando] = useState(false)

  const cargar = useCallback(async () => {
    try {
      const r = await api.get<MarketingCampaignDetailResponse>(`/marketing/campaigns/${id}`)
      setData(r)
      setForm({
        name: r.campaign.name,
        subject: r.campaign.subject,
        // El cuerpo se escribe en texto plano, igual que en el compositor del
        // Buzón: el HTML lo pone el sistema al enviar.
        bodyText: r.campaign.bodyText ?? htmlATexto(r.campaign.bodyHtml),
        senderAccountId: r.campaign.senderAccountId ?? '',
        dailyCap: r.campaign.dailyCap,
        throttleSeconds: r.campaign.throttleSeconds,
        trackClicks: r.campaign.trackClicks,
      })
      setTagIds(r.campaign.tagIds)
      setError(null)
    } catch (err) {
      setError(apiErrorMessage(err, t('campaigns.errors.load')))
    } finally {
      setLoading(false)
    }
  }, [id, t])

  useEffect(() => {
    void cargar()
    void api
      .get<ClientTagListResponse>('/client-tags')
      .then((r) => setTags(r.items))
      .catch(() => setTags([]))
    if (canWrite) {
      void api
        .get<MarketingSenderListResponse>('/marketing/senders')
        .then(setSenders)
        .catch(() => setSenders(null))
      void api
        .get<MarketingTemplateListResponse>('/marketing/templates')
        .then((r) => setPlantillas(r.items.filter((x) => x.channel === 'CORREO')))
        .catch(() => setPlantillas([]))
    }
  }, [cargar, canWrite])

  // Mientras está saliendo, la pantalla se refresca sola: es un proceso que
  // avanza en el servidor y mirar un número congelado no dice nada.
  const enMarcha = data?.campaign.status === 'ENVIANDO' || data?.campaign.status === 'PROGRAMADA'
  useEffect(() => {
    if (!enMarcha) return
    const h = window.setInterval(() => void cargar(), 15000)
    return () => window.clearInterval(h)
  }, [enMarcha, cargar])

  // El remitente elegido, ¿lee sus propios rebotes? Sin cuenta elegida sale por
  // el SMTP del bufete, que no tiene bandeja que sincronizar: tampoco los lee.
  const remitenteCiego = form.senderAccountId
    ? senders?.items.find((s) => s.id === form.senderAccountId)?.sincronizaRebotes === false
    : // Sin cuenta elegida sale por el SMTP del bufete, que no tiene bandeja de
      // entrada: por ahí NUNCA se lee un rebote. El aviso toca siempre, y sobre
      // todo cuando no hay ninguna cuenta conectada, que es el caso de ceguera
      // total y era justo el que se quedaba sin avisar.
      true

  const esBorrador = data?.campaign.status === 'BORRADOR'
  const stats = data?.campaign.stats
  const pendientes = stats?.pendientes ?? 0

  const porcentaje = useMemo(() => {
    if (!stats || stats.total === 0) return 0
    return Math.round(((stats.enviados + stats.fallidos) / stats.total) * 100)
  }, [stats])

  /**
   * Volcar una plantilla sobre el cuerpo. Pisa lo escrito, así que si hay algo
   * se pregunta: perder un correo a medio redactar por un clic en un desplegable
   * sería exactamente el tipo de cosa que hace que nadie vuelva a usarlo.
   */
  const usarPlantilla = async (id: string) => {
    const p = plantillas.find((x) => x.id === id)
    if (!p) return
    if (
      form.bodyText.trim() &&
      !(await confirmDialog({ message: t('campaigns.confirmUseTemplate', { name: p.name }) }))
    ) {
      return
    }
    setForm((f) => ({
      ...f,
      bodyText: p.bodyText,
      // El asunto solo se pisa si está vacío: puede que la campaña ya tenga el suyo.
      subject: f.subject.trim() ? f.subject : (p.subject ?? f.subject),
    }))
    toast.success(t('campaigns.templateApplied', { name: p.name }))
  }

  const guardar = async () => {
    setBusy(true)
    try {
      await api.patch(`/marketing/campaigns/${id}`, {
        ...(esBorrador
          ? {
              name: form.name,
              subject: form.subject,
              bodyHtml: textoAHtml(form.bodyText),
              bodyText: form.bodyText,
              senderAccountId: form.senderAccountId || null,
              trackClicks: form.trackClicks,
            }
          : {}),
        dailyCap: form.dailyCap,
        throttleSeconds: form.throttleSeconds,
      })
      toast.success(t('campaigns.saved'))
      await cargar()
    } catch (err) {
      toast.error(apiErrorMessage(err, t('campaigns.errors.generic')))
    } finally {
      setBusy(false)
    }
  }

  const recalcularLista = async () => {
    setBusy(true)
    try {
      const r = await api.put<SetMarketingAudienceResponse>(
        `/marketing/campaigns/${id}/audience`,
        { tagIds, extraEmails },
      )
      setUltimaAudiencia(r)
      await cargar()
    } catch (err) {
      toast.error(apiErrorMessage(err, t('campaigns.errors.generic')))
    } finally {
      setBusy(false)
    }
  }

  const accion = async (ruta: string, mensaje: string) => {
    setBusy(true)
    try {
      await api.post(`/marketing/campaigns/${id}/${ruta}`, {})
      toast.success(mensaje)
      await cargar()
    } catch (err) {
      toast.error(apiErrorMessage(err, t('campaigns.errors.generic')))
    } finally {
      setBusy(false)
    }
  }

  const borrar = async () => {
    if (!(await confirmDialog({ message: t('campaigns.confirmDelete'), tone: 'danger' }))) return
    try {
      await api.delete(`/marketing/campaigns/${id}`)
      navigate('/marketing/campaigns')
    } catch (err) {
      toast.error(apiErrorMessage(err, t('campaigns.errors.generic')))
    }
  }

  if (loading) {
    return (
      <div className="surface p-10 text-center t-muted">
        <Loader2 size={18} className="inline-block animate-spin" /> {t('common:loading')}
      </div>
    )
  }
  if (error || !data) {
    return (
      <div className="surface p-3 text-sm rounded-lg border text-rose-700 dark:text-rose-300 bg-rose-500/10 border-rose-500/30 flex items-center gap-2">
        <AlertCircle size={14} /> {error ?? t('campaigns.errors.load')}
      </div>
    )
  }

  const c = data.campaign
  const inputCls =
    'w-full px-3 py-2 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-primary text-sm focus:outline-none focus:border-toga-500/50 disabled:opacity-60'

  return (
    <div className="space-y-4 animate-slideUp">
      <header className="flex flex-wrap items-start gap-3 justify-between">
        <div className="min-w-0">
          <Link
            to="/marketing/campaigns"
            className="text-xs t-muted hover:t-primary inline-flex items-center gap-1"
          >
            <ArrowLeft size={12} /> {t('campaigns.backToList')}
          </Link>
          <h1 className="text-xl font-serif font-semibold t-primary flex items-center gap-2 mt-1">
            {c.name}
            <span className={`px-1.5 py-0.5 rounded border text-xs font-sans ${PILL_CAMPAIGN[c.status]}`}>
              {t(`campaigns.status.${c.status}`)}
            </span>
          </h1>
          <p className="text-xs t-muted">
            {c.fromEmail
              ? t('campaigns.sentFrom', { email: c.fromEmail })
              : t('campaigns.notSentYet')}
          </p>
        </div>

        <div className="flex items-center gap-2">
          {esBorrador && canSend && (
            <button
              type="button"
              disabled={busy || pendientes === 0}
              onClick={() => setLanzando(true)}
              className="px-3.5 py-1.5 rounded-lg text-sm font-semibold text-white bg-toga-600 hover:bg-toga-700 disabled:opacity-50 flex items-center gap-1.5"
            >
              <Send size={14} /> {t('campaigns.launch')}
            </button>
          )}
          {canSend && (c.status === 'ENVIANDO' || c.status === 'PROGRAMADA') && (
            <button
              type="button"
              disabled={busy}
              onClick={() => void accion('pause', t('campaigns.paused'))}
              className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover flex items-center gap-1.5"
            >
              <Pause size={14} /> {t('campaigns.pause')}
            </button>
          )}
          {canSend && c.status === 'PAUSADA' && (
            <button
              type="button"
              disabled={busy}
              onClick={() => void accion('resume', t('campaigns.resumed'))}
              className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover flex items-center gap-1.5"
            >
              <Play size={14} /> {t('campaigns.resume')}
            </button>
          )}
          {canSend && ['PROGRAMADA', 'ENVIANDO', 'PAUSADA'].includes(c.status) && (
            <button
              type="button"
              disabled={busy}
              onClick={() => void accion('cancel', t('campaigns.cancelled'))}
              className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover hover:text-rose-600 flex items-center gap-1.5"
            >
              <X size={14} /> {t('campaigns.cancel')}
            </button>
          )}
          {esBorrador && canWrite && (
            <button
              type="button"
              onClick={() => void borrar()}
              title={t('common:actions.delete')}
              className="p-2 rounded-lg t-muted hover:bg-hover hover:text-rose-600"
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </header>

      {/* Marcador */}
      {stats && stats.total > 0 && (
        <section className="surface p-4 space-y-3">
          <div className="flex items-end justify-between gap-3 flex-wrap">
            <div className="text-sm t-secondary">
              {t('campaigns.progress', {
                enviados: stats.enviados,
                total: stats.total,
              })}
            </div>
            <div className="flex gap-3 text-xs">
              <Marcador label={t('campaigns.stats.pending')} valor={stats.pendientes} />
              <Marcador label={t('campaigns.stats.opened')} valor={stats.abiertos} tono="emerald" />
              <Marcador label={t('campaigns.stats.clicked')} valor={stats.clicados} tono="emerald" />
              {stats.fallidos > 0 && (
                <Marcador label={t('campaigns.stats.failed')} valor={stats.fallidos} tono="rose" />
              )}
              {stats.suprimidos > 0 && (
                <Marcador
                  label={t('campaigns.stats.suppressed')}
                  valor={stats.suprimidos}
                  tono="amber"
                />
              )}
            </div>
          </div>
          <div className="h-2 rounded-full bg-[var(--bg-input-soft)] overflow-hidden">
            <div
              className="h-full bg-toga-500 transition-all"
              style={{ width: `${porcentaje}%` }}
            />
          </div>
          {c.status === 'ENVIANDO' && stats.pendientes > 0 && (
            <p className="text-xs t-faint">
              {t('campaigns.dripHint', { seg: c.throttleSeconds, cap: c.dailyCap })}
            </p>
          )}
        </section>
      )}

      {/* El mensaje */}
      <section className="surface p-4 space-y-3">
        <h2 className="font-semibold t-primary text-sm">{t('campaigns.messageTitle')}</h2>
        {!esBorrador && (
          <div className="flex items-start gap-2 text-xs rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-amber-700 dark:text-amber-300">
            <AlertTriangle size={13} className="shrink-0 mt-0.5" />
            <span>{t('campaigns.frozenHint')}</span>
          </div>
        )}
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="space-y-1.5 block">
            <span className="text-sm font-medium t-primary">{t('campaigns.new.nameLabel')}</span>
            <input
              value={form.name}
              disabled={!esBorrador || !canWrite}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className={inputCls}
            />
          </label>
          <label className="space-y-1.5 block">
            <span className="text-sm font-medium t-primary">{t('campaigns.senderLabel')}</span>
            <select
              value={form.senderAccountId}
              disabled={!esBorrador || !canWrite}
              onChange={(e) => setForm({ ...form, senderAccountId: e.target.value })}
              className={inputCls}
            >
              <option value="">
                {senders?.firmFromEmail
                  ? t('campaigns.senderFirm', { email: senders.firmFromEmail })
                  : t('campaigns.senderFirmNone')}
              </option>
              {(senders?.items ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.emailAddress} · {s.ownerName}
                  {s.conProblemas ? ` — ${t('campaigns.senderBroken')}` : ''}
                </option>
              ))}
            </select>
            {/* Los rebotes llegan a la bandeja de la cuenta remitente. Si esa
                cuenta no se sincroniza, las direcciones muertas se quedan en la
                lista para siempre y nadie se entera: hay que decirlo aquí, no
                descubrirlo cuando Google corte la cuenta. */}
            {remitenteCiego && (
              <span className="text-xs text-amber-700 dark:text-amber-300 flex items-start gap-1.5">
                <AlertTriangle size={12} className="shrink-0 mt-0.5" />
                {t('campaigns.senderNoBounces')}
              </span>
            )}
          </label>
        </div>
        <label className="space-y-1.5 block">
          <span className="text-sm font-medium t-primary">{t('campaigns.subjectLabel')}</span>
          <input
            value={form.subject}
            disabled={!esBorrador || !canWrite}
            onChange={(e) => setForm({ ...form, subject: e.target.value })}
            className={inputCls}
          />
        </label>
        <label className="space-y-1.5 block">
          <span className="text-sm font-medium t-primary flex items-center gap-2">
            {t('campaigns.bodyLabel')}
            {esBorrador && canWrite && (
              <button
                type="button"
                onClick={() => setRedactando(true)}
                className="ml-auto text-xs font-normal px-2 py-1 rounded border border-base t-secondary hover:bg-hover inline-flex items-center gap-1"
              >
                <Sparkles size={11} /> {t('campaigns.ai.button')}
              </button>
            )}
            {esBorrador && canWrite && plantillas.length > 0 && (
              <select
                value=""
                onChange={(e) => void usarPlantilla(e.target.value)}
                className="text-xs font-normal px-2 py-1 rounded border border-base bg-transparent t-secondary focus:outline-none"
              >
                <option value="">{t('campaigns.useTemplate')}</option>
                {plantillas.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            )}
          </span>
          <textarea
            value={form.bodyText}
            disabled={!esBorrador || !canWrite}
            onChange={(e) => setForm({ ...form, bodyText: e.target.value })}
            rows={10}
            className={inputCls}
          />
          {/* Las dos variables se pasan como valores para que i18next las pinte
              tal cual: escritas dentro del texto traducido, las sustituiría por
              vacío y el consejo diría «puedes usar  y ». */}
          {/* Las variables se pasan como VALORES: escritas dentro del texto
              traducido, i18next las sustituiría por vacío y el consejo diría
              «puedes usar , , y ». Y salen de la misma lista que usa el motor. */}
          <span className="text-xs t-faint block">
            {t('campaigns.bodyHint', {
              var1: `{{${VARIABLES_DE_CAMPANA[0]!.clave}}}`,
              var2: `{{${VARIABLES_DE_CAMPANA[1]!.clave}}}`,
              var3: `{{${VARIABLES_DE_CAMPANA[2]!.clave}}}`,
              var4: `{{${VARIABLES_DE_CAMPANA[3]!.clave}}}`,
            })}
          </span>
          {/* ☠️ SIEMPRE, también cuando son cero. Un cero callado parece un
              resultado; un cero dicho es una pregunta («¿por qué no hay
              enlaces?»). Es exactamente el silencio que ocultó durante horas
              que el medidor no podía funcionar. */}
          <span className="text-xs t-faint flex items-center gap-1">
            <MousePointerClick size={11} className="shrink-0" />
            {!form.trackClicks
              ? t('campaigns.linksOff')
              : data.enlacesMedidos === 0 && data.enlacesSinMedir === 0
                ? t('campaigns.linksNone')
                : t('campaigns.linksMeasured', {
                    n: data.enlacesMedidos,
                    sin: data.enlacesSinMedir,
                  })}
          </span>
        </label>

        <div className="grid sm:grid-cols-2 gap-3">
          <label className="space-y-1.5 block">
            <span className="text-sm font-medium t-primary">{t('campaigns.dailyCapLabel')}</span>
            <input
              type="number"
              min={1}
              max={MARKETING_DAILY_CAP_MAX}
              value={form.dailyCap}
              disabled={!canWrite}
              onChange={(e) => setForm({ ...form, dailyCap: Number(e.target.value) })}
              className={inputCls}
            />
          </label>
          <label className="space-y-1.5 block">
            <span className="text-sm font-medium t-primary">{t('campaigns.throttleLabel')}</span>
            <input
              type="number"
              min={MARKETING_THROTTLE_MIN}
              value={form.throttleSeconds}
              disabled={!canWrite}
              onChange={(e) => setForm({ ...form, throttleSeconds: Number(e.target.value) })}
              className={inputCls}
            />
          </label>
        </div>
        <p className="text-xs t-faint">{t('campaigns.capHint')}</p>

        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.trackClicks}
            disabled={!esBorrador || !canWrite}
            onChange={(e) => setForm({ ...form, trackClicks: e.target.checked })}
            className="mt-0.5 accent-toga-600 disabled:opacity-50"
          />
          <span className="min-w-0">
            <span className="t-primary">{t('campaigns.trackClicksLabel')}</span>
            <span className="block text-xs t-faint">{t('campaigns.trackClicksHint')}</span>
          </span>
        </label>

        {canWrite && (
          <div className="flex justify-end">
            <button
              type="button"
              disabled={busy}
              onClick={() => void guardar()}
              className="px-3.5 py-2 rounded-lg text-sm font-semibold text-white bg-toga-600 hover:bg-toga-700 disabled:opacity-50 inline-flex items-center gap-2"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {t('common:actions.save')}
            </button>
          </div>
        )}
      </section>

      {/* La lista */}
      <section className="surface p-4 space-y-3">
        <h2 className="font-semibold t-primary text-sm flex items-center gap-2">
          <Users size={15} /> {t('campaigns.audienceTitle')}
        </h2>
        {esBorrador && canWrite ? (
          <>
            <div className="flex flex-wrap gap-1.5">
              {tags.length === 0 && <span className="text-xs t-faint">{t('campaigns.noTags')}</span>}
              {tags.map((tag) => {
                const puesta = tagIds.includes(tag.id)
                return (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() =>
                      setTagIds(
                        puesta ? tagIds.filter((x) => x !== tag.id) : [...tagIds, tag.id],
                      )
                    }
                    className={`px-2 py-1 rounded-full border text-xs transition ${
                      puesta
                        ? 'bg-toga-500/20 text-toga-700 dark:text-toga-200 border-toga-500/50'
                        : 't-secondary border-base hover:bg-hover'
                    }`}
                  >
                    {tag.name}
                    {tag.clientCount !== undefined && (
                      <span className="t-faint"> · {tag.clientCount}</span>
                    )}
                  </button>
                )
              })}
            </div>
            <label className="space-y-1.5 block">
              <span className="text-sm font-medium t-primary">{t('campaigns.extraEmails')}</span>
              <textarea
                value={extraEmails}
                onChange={(e) => setExtraEmails(e.target.value)}
                rows={2}
                placeholder={t('campaigns.extraEmailsPlaceholder')}
                className={`${inputCls} font-mono`}
              />
            </label>
            <div className="flex justify-end">
              <button
                type="button"
                disabled={busy}
                onClick={() => void recalcularLista()}
                className="px-3 py-2 rounded-lg text-sm t-secondary border border-base hover:bg-hover inline-flex items-center gap-2 disabled:opacity-50"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Users size={14} />}
                {t('campaigns.rebuild')}
              </button>
            </div>

            {ultimaAudiencia && (
              <div className="space-y-1.5 text-xs">
                <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-300">
                  <CheckCircle2 size={13} />
                  {t('campaigns.audienceResult', {
                    total: ultimaAudiencia.total,
                    agregados: ultimaAudiencia.agregados,
                    quitados: ultimaAudiencia.quitados,
                  })}
                </div>
                {ultimaAudiencia.suprimidos.length > 0 && (
                  <div className="flex items-start gap-2 text-amber-700 dark:text-amber-300">
                    <Ban size={13} className="shrink-0 mt-0.5" />
                    <span>
                      {t('campaigns.audienceSuppressed', {
                        list: ultimaAudiencia.suprimidos.join(', '),
                      })}
                    </span>
                  </div>
                )}
                {ultimaAudiencia.sinCorreo.length > 0 && (
                  <div className="t-faint">
                    {t('campaigns.audienceNoEmail', {
                      list: ultimaAudiencia.sinCorreo.join(', '),
                    })}
                  </div>
                )}
                {ultimaAudiencia.invalidos.length > 0 && (
                  <div className="text-rose-600 dark:text-rose-300">
                    {t('campaigns.audienceInvalid', {
                      list: ultimaAudiencia.invalidos.join(', '),
                    })}
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <p className="text-xs t-faint">{t('campaigns.audienceLockedHint')}</p>
        )}

        <div className="max-h-96 overflow-auto rounded-lg border border-base">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-[var(--bg-surface)] z-10">
              <tr className="text-left t-muted">
                <th className="px-2 py-1.5 font-medium">{t('campaigns.table.recipient')}</th>
                <th className="px-2 py-1.5 font-medium">{t('campaigns.table.status')}</th>
                <th className="px-2 py-1.5 font-medium text-right">{t('campaigns.table.opens')}</th>
                <th className="px-2 py-1.5 font-medium text-right">{t('campaigns.table.clicks')}</th>
              </tr>
            </thead>
            <tbody>
              {data.recipients.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-2 py-6 text-center t-faint">
                    {t('campaigns.noRecipients')}
                  </td>
                </tr>
              )}
              {data.recipients.map((r) => (
                <tr key={r.id} className="border-t border-base/60">
                  <td className="px-2 py-1.5">
                    {r.clientId ? (
                      <Link
                        to={`/clients/${r.clientId}`}
                        className="t-primary hover:underline"
                      >
                        {r.displayName || r.email}
                      </Link>
                    ) : (
                      <span className="t-primary">{r.displayName || r.email}</span>
                    )}
                    {/* La dirección solo se repite debajo si arriba va un
                        nombre: si no, se leía dos veces la misma línea. */}
                    {r.displayName && <div className="t-faint break-all">{r.email}</div>}
                    {r.errorMessage && (
                      <div className="text-rose-600 dark:text-rose-300">{r.errorMessage}</div>
                    )}
                  </td>
                  <td className="px-2 py-1.5">
                    <span
                      className={`px-1.5 py-0.5 rounded border whitespace-nowrap ${PILL_RECIPIENT[r.status]}`}
                    >
                      {t(`campaigns.recipientStatus.${r.status}`)}
                    </span>
                    {r.sentAt && (
                      <div className="t-faint mt-0.5">
                        {new Date(r.sentAt).toLocaleString(i18n.language)}
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums t-secondary">
                    {r.opens > 0 ? r.opens : '—'}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums">
                    {r.clicks > 0 ? (
                      <span className="text-emerald-700 dark:text-emerald-300 font-semibold">
                        {r.clicks}
                      </span>
                    ) : (
                      <span className="t-faint">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {redactando && (
        <DraftModal
          id={id}
          onClose={() => setRedactando(false)}
          onDraft={(r) => {
            setRedactando(false)
            setForm((f) => ({
              ...f,
              bodyText: r.bodyText,
              // El asunto solo se pisa si está vacío o si es el que se puso al
              // crear la campaña sin pensar: lo que ya haya escrito manda.
              subject: f.subject.trim() ? f.subject : r.subject,
            }))
            toast.success(t('campaigns.ai.done'))
          }}
        />
      )}

      {lanzando && (
        <LaunchModal
          id={id}
          pendientes={pendientes}
          enviadosHoy={data.enviadosHoyEnTotal}
          onClose={() => setLanzando(false)}
          onLaunched={() => {
            setLanzando(false)
            void cargar()
          }}
        />
      )}
    </div>
  )
}

/**
 * Pedirle a la IA un primer borrador.
 *
 * ☠️ Lo que vuelve NO se guarda: rellena el formulario y quien redacta decide.
 * Una IA que escribe directamente en algo que va a salir a ciento cincuenta
 * personas es mala idea aunque escriba bien.
 */
function DraftModal({
  id,
  onClose,
  onDraft,
}: {
  id: string
  onClose: () => void
  onDraft: (r: DraftMarketingCampaignResponse) => void
}) {
  const { t } = useTranslation(['marketing', 'common'])
  const [instrucciones, setInstrucciones] = useState('')
  const [busy, setBusy] = useState(false)

  const pedir = async () => {
    setBusy(true)
    try {
      onDraft(
        await api.post<DraftMarketingCampaignResponse>(`/marketing/campaigns/${id}/draft`, {
          instructions: instrucciones,
        }),
      )
    } catch (err) {
      toast.error(apiErrorMessage(err, t('campaigns.ai.failed')))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal open onClose={onClose} title={t('campaigns.ai.title')} maxWidth="max-w-lg">
      <div className="space-y-4">
        <p className="text-sm t-secondary">{t('campaigns.ai.hint')}</p>
        <textarea
          value={instrucciones}
          onChange={(e) => setInstrucciones(e.target.value)}
          rows={5}
          placeholder={t('campaigns.ai.placeholder')}
          className="w-full px-3 py-2 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-primary text-sm focus:outline-none focus:border-toga-500/50"
          autoFocus
        />
        <p className="text-xs t-faint">{t('campaigns.ai.footer')}</p>
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-2 rounded-lg text-sm t-secondary hover:bg-hover"
          >
            {t('common:actions.cancel')}
          </button>
          <button
            type="button"
            disabled={busy || instrucciones.trim().length < 10}
            onClick={() => void pedir()}
            className="px-3.5 py-2 rounded-lg text-sm font-semibold text-white bg-toga-600 hover:bg-toga-700 disabled:opacity-50 inline-flex items-center gap-2"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            {t('campaigns.ai.submit')}
          </button>
        </div>
      </div>
    </Modal>
  )
}

function Marcador({
  label,
  valor,
  tono,
}: {
  label: string
  valor: number
  tono?: 'emerald' | 'rose' | 'amber'
}) {
  const color =
    tono === 'emerald'
      ? 'text-emerald-700 dark:text-emerald-300'
      : tono === 'rose'
        ? 'text-rose-700 dark:text-rose-300'
        : tono === 'amber'
          ? 'text-amber-700 dark:text-amber-300'
          : 't-secondary'
  return (
    <div className="text-right">
      <div className={`font-semibold tabular-nums ${color}`}>{valor}</div>
      <div className="t-faint">{label}</div>
    </div>
  )
}

/**
 * ☠️ La última pantalla antes de que salga correo de verdad. Pide escribir el
 * número de destinatarios a mano: es la diferencia entre revisar la cifra y
 * darle a un botón sin mirarla, y a partir de aquí ya no se puede recoger.
 */
function LaunchModal({
  id,
  pendientes,
  enviadosHoy,
  onClose,
  onLaunched,
}: {
  id: string
  pendientes: number
  enviadosHoy: number
  onClose: () => void
  onLaunched: () => void
}) {
  const { t } = useTranslation(['marketing', 'common'])
  const [confirmacion, setConfirmacion] = useState('')
  const [busy, setBusy] = useState(false)

  const lanzar = async () => {
    setBusy(true)
    try {
      await api.post(`/marketing/campaigns/${id}/start`, { expectedRecipients: pendientes })
      toast.success(t('campaigns.launched', { n: pendientes }))
      onLaunched()
    } catch (err) {
      toast.error(apiErrorMessage(err, t('campaigns.errors.generic')))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal open onClose={onClose} title={t('campaigns.launchModal.title')} maxWidth="max-w-lg">
      <div className="space-y-4">
        <div className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-700 dark:text-amber-300">
          <AlertTriangle size={16} className="shrink-0 mt-0.5" />
          <p>{t('campaigns.launchModal.warning', { n: pendientes })}</p>
        </div>
        {/* El tope de cada campaña no ve a las demás, y el cupo de Google es de
            la cuenta entera. No bloquea —a este volumen sería un candado
            inútil— pero se dice cuando importa. */}
        {enviadosHoy > 0 && (
          <p className="text-xs t-faint">
            {t('campaigns.launchModal.todayHint', { hoy: enviadosHoy, ahora: pendientes })}
          </p>
        )}
        <label className="space-y-1.5 block">
          <span className="text-sm font-medium t-primary">
            {t('campaigns.launchModal.confirmLabel', { n: pendientes })}
          </span>
          <input
            value={confirmacion}
            onChange={(e) => setConfirmacion(e.target.value)}
            inputMode="numeric"
            className="w-full px-3 py-2 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-primary text-sm focus:outline-none focus:border-toga-500/50"
          />
        </label>
        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-2 rounded-lg text-sm t-secondary hover:bg-hover">
            {t('common:actions.cancel')}
          </button>
          <button
            type="button"
            disabled={busy || confirmacion.trim() !== String(pendientes)}
            onClick={() => void lanzar()}
            className="px-3.5 py-2 rounded-lg text-sm font-semibold text-white bg-toga-600 hover:bg-toga-700 disabled:opacity-50 inline-flex items-center gap-2"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {t('campaigns.launchModal.submit')}
          </button>
        </div>
      </div>
    </Modal>
  )
}
